import React from "react";

const timezones = [
  "Asia/Karachi",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata"
];

// Professional sending-rate presets. Sending in a controlled, throttled way
// (instead of blasting the whole list at once) is a real deliverability best
// practice: it keeps you under mailbox-provider rate limits and greatly
// reduces the chance of landing in spam.
const RATE_PRESETS = [
  {
    value: "fast",
    label: "Fast",
    hint: "Send as fast as possible — best for fully warmed, verified domains.",
    perMinute: 0
  },
  {
    value: "balanced",
    label: "Balanced",
    hint: "~60 emails / minute. Recommended safe speed for most senders.",
    perMinute: 60
  },
  {
    value: "safe",
    label: "Safe warm-up",
    hint: "~20 emails / minute. Best for new domains or new IPs.",
    perMinute: 20
  },
  {
    value: "custom",
    label: "Custom",
    hint: "Set your own emails-per-minute limit.",
    perMinute: null
  }
];

export default function ScheduleStep({
  form,
  updateForm,
  updateTracking
}) {
  const tracking = form.tracking || {};
  const sendRate = form.sendRate || "balanced";

  return (
    <div className="sx-schedule-layout">
      <div className="sx-schedule-header">
        <h2>Schedule</h2>
        <p>
          Choose when this campaign should send and configure tracking options.
        </p>
      </div>

      <div className="sx-schedule-grid">
        <section className="sx-schedule-card">
          <h3>Send Settings</h3>

          <div className="sx-schedule-options">
            <button
              type="button"
              className={form.scheduleType === "now" ? "active" : ""}
              onClick={() => updateForm("scheduleType", "now")}
            >
              <span>Send Now</span>
              <small>
                Campaign will be added to the sending queue immediately.
              </small>
            </button>

            <button
              type="button"
              className={form.scheduleType === "later" ? "active" : ""}
              onClick={() => updateForm("scheduleType", "later")}
            >
              <span>Schedule Later</span>
              <small>
                Campaign will send automatically at your selected date and time.
              </small>
            </button>
          </div>

          {form.scheduleType === "later" && (
            <div className="sx-date-time-grid">
              <div>
                <label>Date</label>
                <input
                  type="date"
                  value={form.scheduledDate || ""}
                  onChange={(e) =>
                    updateForm("scheduledDate", e.target.value)
                  }
                />
              </div>

              <div>
                <label>Time</label>
                <input
                  type="time"
                  value={form.scheduledTime || ""}
                  onChange={(e) =>
                    updateForm("scheduledTime", e.target.value)
                  }
                />
              </div>

              <div className="sx-timezone-field">
                <label>Timezone</label>
                <select
                  value={form.timezone || "Asia/Karachi"}
                  onChange={(e) =>
                    updateForm("timezone", e.target.value)
                  }
                >
                  {timezones.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="sx-send-rate">
            <div className="sx-send-rate-head">
              <h4>Sending Speed</h4>
              <p>
                Send your emails at a controlled speed for better inbox
                placement and to stay within provider limits.
              </p>
            </div>

            <div className="sx-send-rate-options">
              {RATE_PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.value}
                  className={sendRate === p.value ? "active" : ""}
                  onClick={() => {
                    updateForm("sendRate", p.value);
                    if (p.value !== "custom") {
                      updateForm("sendRatePerMinute", p.perMinute);
                    }
                  }}
                >
                  <span>{p.label}</span>
                  <small>{p.hint}</small>
                </button>
              ))}
            </div>

            {sendRate === "custom" && (
              <div className="sx-send-rate-custom">
                <label>Emails per minute</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={form.sendRatePerMinute || 30}
                  onChange={(e) =>
                    updateForm(
                      "sendRatePerMinute",
                      Math.max(1, Number(e.target.value) || 1)
                    )
                  }
                />
                <small>
                  Emails will be paced evenly across each minute (e.g. 60 = one
                  email per second).
                </small>
              </div>
            )}

            <div className="sx-send-gap">
              <div className="sx-send-rate-head">
                <h4>Gap Between Each Email</h4>
                <p>
                  Wait a fixed time after every email before sending the next
                  one. Example: set 10 &rarr; 1st email goes now, then it waits
                  10 minutes, sends the 2nd, waits 10 minutes, sends the 3rd, and
                  so on. Set 0 to turn the gap off.
                </p>
              </div>

              <div className="sx-send-gap-presets">
                {[0, 5, 10, 30, 60].map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={Number(form.sendGapMinutes || 0) === m ? "active" : ""}
                    onClick={() => updateForm("sendGapMinutes", m)}
                  >
                    {m === 0 ? "Off" : `${m} min`}
                  </button>
                ))}
              </div>

              <div className="sx-send-rate-custom">
                <label>Minutes between emails</label>
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={form.sendGapMinutes || 0}
                  onChange={(e) =>
                    updateForm(
                      "sendGapMinutes",
                      Math.max(0, Math.min(1440, Number(e.target.value) || 0))
                    )
                  }
                />
                <small>
                  Each email is scheduled at its own time, so long gaps keep
                  working even if the server restarts.
                </small>
              </div>
            </div>
          </div>
        </section>

        <section className="sx-schedule-card">
          <h3>Tracking Configuration</h3>

          <div className="sx-tracking-list">
            <label>
              <input
                type="checkbox"
                checked={!!tracking.openTracking}
                onChange={(e) =>
                  updateTracking("openTracking", e.target.checked)
                }
              />

              <div>
                <b>Open Tracking</b>
                <span>
                  Track when recipients open your campaign emails.
                </span>
              </div>
            </label>

            <label>
              <input
                type="checkbox"
                checked={!!tracking.clickTracking}
                onChange={(e) =>
                  updateTracking("clickTracking", e.target.checked)
                }
              />

              <div>
                <b>Click Tracking</b>
                <span>
                  Track clicks on links inside your campaign emails.
                </span>
              </div>
            </label>

            <label>
              <input
                type="checkbox"
                checked={!!tracking.googleAnalytics}
                onChange={(e) =>
                  updateTracking("googleAnalytics", e.target.checked)
                }
              />

              <div>
                <b>Google Analytics</b>
                <span>
                  Add campaign tracking parameters to supported links.
                </span>
              </div>
            </label>

            <label>
              <input
                type="checkbox"
                checked={!!tracking.unsubscribeTracking}
                onChange={(e) =>
                  updateTracking("unsubscribeTracking", e.target.checked)
                }
              />

              <div>
                <b>Unsubscribe Tracking</b>
                <span>
                  Record unsubscribe events from this campaign.
                </span>
              </div>
            </label>
          </div>
        </section>
      </div>

      <section className="sx-schedule-summary">
        <h3>Final Send Summary</h3>

        <div>
          <p>
            <span>Campaign</span>
            <b>{form.name || "-"}</b>
          </p>

          <p>
            <span>Subject</span>
            <b>{form.subject || "-"}</b>
          </p>

          <p>
            <span>{Array.isArray(form.selectedSenders) && form.selectedSenders.length > 1 ? "Senders" : "Sender"}</span>
            <b>
              {Array.isArray(form.selectedSenders) && form.selectedSenders.length
                ? form.selectedSenders
                    .map(
                      (s) =>
                        `${s.name || s.senderName || "Sender"} <${s.email || s.senderEmail || s.fromEmail || "-"}>`
                    )
                    .join(", ")
                : `${form.fromName || "Sender"} <${form.fromEmail || "-"}>`}
            </b>
          </p>

          <p>
            <span>Send Mode</span>
            <b>
              {form.scheduleType === "later"
                ? "Schedule Later"
                : "Send Now"}
            </b>
          </p>

          <p>
            <span>Email Gap</span>
            <strong>
              {Number(form.sendGapMinutes || 0) > 0
                ? `${form.sendGapMinutes} min between each email`
                : "Off (no gap)"}
            </strong>
          </p>
          <p>
            <span>Sending Speed</span>
            <b>
              {sendRate === "fast"
                ? "Fast (no limit)"
                : sendRate === "custom"
                ? `Custom — ${form.sendRatePerMinute || 30}/min`
                : sendRate === "safe"
                ? "Safe warm-up — ~20/min"
                : "Balanced — ~60/min"}
            </b>
          </p>

          {form.scheduleType === "later" && (
            <p>
              <span>Scheduled For</span>
              <b>
                {form.scheduledDate || "-"} {form.scheduledTime || ""}{" "}
                {form.timezone || ""}
              </b>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
